import { Copy, Link2, RefreshCw } from 'lucide-react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import type { FeedChannel, FeedChannelStatus } from '@nucleus-commerce/api-client/admin';
import { Badge } from '@nucleus-commerce/ui/components/badge';
import { Button } from '@nucleus-commerce/ui/components/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@nucleus-commerce/ui/components/card';
import { toast } from '@nucleus-commerce/ui/components/sonner';
import { cn } from '@nucleus-commerce/ui/lib/utils';

export interface FeedChannelCardProps {
  channel: FeedChannel;
  status: FeedChannelStatus;
  /** Shows the create or rotate action; false for read-only roles and closed stores. */
  editable: boolean;
  rotating: boolean;
  onRotate: (channel: FeedChannel) => void;
  /** The one card the feeds tour points into. */
  tour?: boolean;
}

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex flex-col gap-1 text-sm">
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="min-w-0">{children}</dd>
    </div>
  );
}

/** One channel's feed: its URL to paste into the channel, what it lists and what it leaves out. */
export function FeedChannelCard({
  channel,
  status,
  editable,
  rotating,
  onRotate,
  tour,
}: FeedChannelCardProps) {
  const { t } = useTranslation('feeds');
  const name = t(`channels.${channel}.name`);
  const { url } = status;

  const copy = async (value: string) => {
    try {
      await navigator.clipboard.writeText(value);
      toast.success(t('card.copied'));
    } catch {
      toast.error(t('card.copyFailed'));
    }
  };

  return (
    <Card
      role="region"
      aria-label={name}
      className="gap-4"
      data-tour={tour ? 'settings.feeds.channel' : undefined}
    >
      <CardHeader>
        <CardTitle className="flex flex-wrap items-center gap-2 text-[15px]">
          {name}
          <Badge
            variant="outline"
            className={cn(
              status.enabled
                ? 'bg-success/15 text-success dark:bg-success/20 border-transparent'
                : 'border-border text-muted-foreground',
            )}
          >
            {status.enabled ? t('card.enabled') : t('card.notSetUp')}
          </Badge>
        </CardTitle>
        <CardDescription>{t(`channels.${channel}.hint`)}</CardDescription>
      </CardHeader>
      <CardContent>
        <dl className="flex flex-col gap-4">
          <Row label={t('card.url')}>
            {url ? (
              <div className="flex items-start gap-2">
                <code
                  className="bg-muted min-w-0 flex-1 rounded-md px-2 py-1.5 font-mono text-xs break-all select-all"
                  data-testid={`feed-url-${channel}`}
                >
                  {url}
                </code>
                <Button
                  type="button"
                  variant="outline"
                  size="icon-sm"
                  aria-label={t('card.copy', { channel: name })}
                  onClick={() => void copy(url)}
                  data-tour={tour ? 'settings.feeds.copy-url' : undefined}
                >
                  <Copy aria-hidden />
                </Button>
              </div>
            ) : (
              <span className="text-muted-foreground">{t('card.noUrl')}</span>
            )}
          </Row>
          <Row label={t('card.items')}>
            <span className="tabular-nums">{t('card.itemCount', { count: status.itemCount })}</span>
          </Row>
          <Row label={t('card.skipped')}>
            {status.skipped.length === 0 ? (
              <span className="text-muted-foreground">{t('card.nothingSkipped')}</span>
            ) : (
              <ul
                className="flex flex-col gap-2"
                aria-label={t('card.skippedList', { channel: name })}
              >
                {status.skipped.map(({ reason, count }) => (
                  <li key={reason} className="flex flex-col gap-0.5">
                    <span className="font-medium">
                      {t(`reasons.${reason}.label`)}{' '}
                      <span className="text-muted-foreground tabular-nums">
                        {t('card.productCount', { count })}
                      </span>
                    </span>
                    <span className="text-muted-foreground text-xs">
                      {t(`reasons.${reason}.detail`)}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </Row>
        </dl>
      </CardContent>
      {editable ? (
        <CardFooter>
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={rotating}
            onClick={() => onRotate(channel)}
            data-tour={tour ? 'settings.feeds.rotate' : undefined}
          >
            {status.enabled ? <RefreshCw aria-hidden /> : <Link2 aria-hidden />}
            {rotating ? t('card.working') : status.enabled ? t('card.rotate') : t('card.create')}
          </Button>
        </CardFooter>
      ) : null}
    </Card>
  );
}
